import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useMemo,
} from "react"
import Cookies from "js-cookie"
import costPerMile from "../constants/cost_per_mile.json"
import geo from "../services/geoLocation"
import { GEO_FETCH_DELAY_MS } from "../constants/geo"

interface GeoDataContextType {
  geoCostPerMile: string
  geoCity: string
  geoSessionValue: string | null
  isUSVisitor: boolean
  isLoading: boolean
  hasInitialized: boolean
}

const GeoDataContext = createContext<GeoDataContextType | undefined>(undefined)

interface GeoDataProviderProps {
  children: ReactNode
}

export const GeoDataProvider: React.FC<GeoDataProviderProps> = ({
  children,
}) => {
  const [geoSessionValue, setGeoSessionValue] = useState(
    Cookies.get("geoLocation") || null,
  )
  const [isUSVisitor, setIsUSVisitor] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [hasInitialized, setHasInitialized] = useState(false)
  const [geoCostPerMile, setGeoCostPerMile] = useState("")
  const [geoCity, setGeoCity] = useState("")

  useEffect(() => {
    // Process cached data immediately if available
    const processCachedData = (): boolean => {
      if (!geoSessionValue) return false

      try {
        const geoData = JSON.parse(geoSessionValue)
        if (geoData && geoData.country_code2 === "US") {
          const city = geoData.city || ""
          setGeoCity(city)

          const matchingLocation = costPerMile.find(
            (location) => location.city.toLowerCase() === city.toLowerCase(),
          )
          const cost_per_mile = matchingLocation
            ? matchingLocation.cost_per_mile
            : costPerMile.find(
                (location) => location.city.toLowerCase() === "overall",
              )?.cost_per_mile

          setGeoCostPerMile(cost_per_mile ?? "")
          setHasInitialized(true)
          setIsLoading(false)
          return true
        } else {
          setIsUSVisitor(false)
          setHasInitialized(true)
          setIsLoading(false)
          return true
        }
      } catch (error) {
        console.warn("Error parsing geoSessionValue:", error)
        Cookies.remove("geoLocation")
        setGeoSessionValue(null)
        return false
      }
    }

    // If we have cached data, use it immediately
    if (processCachedData()) {
      return
    }

    // Mark as initialized with defaults while we wait for geo data
    setHasInitialized(true)

    // Defer fresh geo fetch to avoid blocking LCP/TBT
    const timeoutId = setTimeout(async () => {
      setIsLoading(true)

      try {
        const rawGeoData = await geo.getGeoLocation()
        let decodedGeoData = rawGeoData
        let geoData = null

        try {
          if (rawGeoData?.includes("%22")) {
            decodedGeoData = decodeURIComponent(rawGeoData)
          }
          geoData = JSON.parse(decodedGeoData!)
        } catch (parseError) {
          console.warn("Error parsing geoData:", parseError)
          setIsLoading(false)
          return
        }

        if (geoData?.country_code2 === "US") {
          Cookies.set("geoLocation", decodedGeoData!, { expires: 7 })
          setGeoSessionValue(decodedGeoData)

          const city = geoData.city || ""
          setGeoCity(city)

          const matchingLocation = costPerMile.find(
            (location) => location.city.toLowerCase() === city.toLowerCase(),
          )
          const cost_per_mile = matchingLocation
            ? matchingLocation.cost_per_mile
            : costPerMile.find(
                (location) => location.city.toLowerCase() === "overall",
              )?.cost_per_mile

          setGeoCostPerMile(cost_per_mile ?? "")
        } else {
          setIsUSVisitor(false)
        }
      } catch (error) {
        console.warn("Error fetching geolocation:", error)
      }

      setIsLoading(false)
    }, GEO_FETCH_DELAY_MS)

    return () => clearTimeout(timeoutId)
  }, [geoSessionValue])

  const memoizedValue = useMemo(() => {
    return {
      geoSessionValue,
      isUSVisitor,
      isLoading,
      hasInitialized,
      geoCostPerMile,
      geoCity,
    }
  }, [
    geoSessionValue,
    isUSVisitor,
    isLoading,
    hasInitialized,
    geoCostPerMile,
    geoCity,
  ])

  return (
    <GeoDataContext.Provider value={memoizedValue}>
      {children}
    </GeoDataContext.Provider>
  )
}

export const useGeoData = (): GeoDataContextType => {
  const context = useContext(GeoDataContext)
  if (context === undefined) {
    throw new Error("useGeoData must be used within a GeoDataProvider")
  }
  return context
}
